import {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from "react";
import projectFactory from "../../other/configs/projectConfig.jsx";
import {SERVER_BASE_URL, ENABLE_WEBSOCKET} from "../../env";
import AuthContext, { UserSpecs } from "./authContext.jsx";
import SwivuStateProvider, { initPersistedStorage } from "../hooks/useViewState/bryntumSwivuStateProvider.jsx";
import useBoards from "../hooks/useBoards.jsx";
import useEditorHandler, { EditorSpecs } from "../hooks/useEditorHandler.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../other/constants/PersistenceStateKeys.jsx";
import {useHistory} from "react-router-dom";
import { getIsArchivedForBoard } from "../../other/utils/otherUtils.jsx";
import {BACKEND_NOTIFICATION_KEYS, TriggerKeyForLastUpdatedAt, ViewType} from "../../other/constants/OtherKeys.jsx";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import { canLoadPage, unHandleErrorListener } from "../../other/utils/domUtils.jsx";
import { MessageDialog, ObjectHelper, StringHelper } from "@bryntum/schedulerpro";
import {
  checkUserEmailAndRole, cleanUpOrphanEventsIfAny,
  getRedirectToAgendaIfCantView,
  isReplayHappen,
  showSessionExpiredAlert,
  showToast
} from "../../other/utils/utils.jsx";
import {networkStatusStore} from "../hooks/useNetworkState.jsx";
import ViewManager from "../../other/feature/views/ViewManager.js";
import SwivuWebSocketService from "../../other/services/SwivuWebSocketService.jsx";
import { addUserInfoSentry } from "../../sentryIntegration.jsx";
import { isOnlyUserPreferencesCall, logSyncErrorToSentry, showRequestFailErrorDialog } from "../../other/utils/projectUtils.jsx";
import SwivuRootMask from "../../bryntum-lib/widget/extendingClasses/SwivuRootMask.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { NotifyeeBoardUser } from "../../bryntum-lib/models/NotifyeeBoardUser.jsx";
import { useEntityManager } from "../hooks/useEntityManager";

const bryntumContextConfig = {
  project: null,
  planProject: null,
  boardManager: null,
  stateProvider: null,
  isAppLoaded: false,
  /**
   * @type {{id: string, name: string, record: object}|null} - record is a Board store record
   * */
  selectedBoard: null,
  editorHandler: EditorSpecs,
  rowHeight: 0,
  globalSearch: null,
  /**
   * @type {Function}
   */
  setGlobalSearch: null,
  user: UserSpecs,
  /**
   * @description - The currentView will be set by each main grid view when rendered to render itself for providing states for current view
   */
  currentViewRef: {
    current: {
      scrollSchedulerTimeAxisToCurrDate: () => {},
      swivuLastViewportCenterDate: null,
    }
  },
  boardSessionIdRef: { current: "" },
  swivuAppGrants: [],
  viewManager: null,
  entityManager: null
};

const BryntumContext = createContext(bryntumContextConfig);

// user preferences (mainly appearance) variables
let userPreferences = null;
let isBeforeUnloadEventAdded = false;


const getSetting = (key, dflt, isBoardPref = false) => {
  const prefStore = isBoardPref ? userPreferences?.boardPreferencesStore: userPreferences;
  if (prefStore) { // this may not be available for preview view which appears behind login popup
    const uPref = prefStore.find(r => r.key === key)
    return uPref ? uPref.value : dflt;
  } else return dflt;
};

const getRowHeight = () => {
  const viewType = getSetting("viewType");
  return getFontSize() * (viewType === ViewType.compact ? 3 :
    viewType === ViewType.compressed ? 2 : 4 );
};

const getResourceMargin = () => {
  const rowHeight = getRowHeight();
  return Math.round(rowHeight / 4);
};

const getStripeFeature = () => {
  return { disabled: getSetting("stripeFeature") !== "true" };
};

const getFontSize = () => {
  return parseInt(
    window.getComputedStyle(document.documentElement).getPropertyValue("font-size"),
    10
  );
};

const getGridLinesPreferenceSetting = () => getSetting("gridlines", String(true)) === String(true);

const setFontSize = (value) => {
  const fontSize = value.isStore
    ? value.find((r) => r.key === "fontSize") || { value: 15 }
    : value;
  document.documentElement.style.fontSize = `${fontSize.value}px`;
};

const destroyProjectsAndStores = (
  { project, planProject, extraStores },
  boardManager
) => {
  planProject?.destroy();
  project?.destroy();
  boardManager.clearBoards();
  if (extraStores) {
    for (const store of Object.values(extraStores)) {
      store.destroy();
    }
  }
};


const BryntumContextProvider = (props) => {
  const currentViewRef = useRef({});
  const history = useHistory();
  const { user, appRolesRef} = useContext(AuthContext);
  const [isAppLoaded, setAppLoaded] = useState(false);
  const [stateProvider, setStateProvider] = useState();
  const [project, setProject] = useState();
  const [planProject, setPlanProject] = useState();
  const [extraStores, setExtraStores] = useState();
  const editorHandler = useEditorHandler();
  const [globalSearch, setGlobalSearch] = useState(null);
  const boardSessionIdRef = useRef("");
  const swivuWebSocketRef = useRef(null);

  /**
   * @description - create project creates new stores for all entities
   * */
  const createProject = useCallback((authUser) => {
    const {
      project: newProject,
      planProject: newPlanProject,
      extraStores: newExtraStores
    } = projectFactory(authUser);
    setProject(newProject);
    setPlanProject(newPlanProject);
    setExtraStores(newExtraStores);
    return { newProject, newPlanProject, newExtraStores };
  }, []);

  const [viewManager, setViewManager] = useState();
  const entityManager = useEntityManager({ viewManager });


  /**
   * @description - when a board is selected, this function loads the entire board data into
   *                the various stores.
   */
  // Todo: find why this function also being called when logout
  const onBoardSelected = useCallback(
    async (board) => {
      window.SwivuAppLoaderInstance.show();
      setAppLoaded(false);
      if (!board || !user) {
        window.SwivuAppLoaderInstance.hide();
        if (swivuWebSocketRef.current) {
          swivuWebSocketRef.current.destroy();
          swivuWebSocketRef.current = null;
        }
        return;
      }

      // this will update user permissions for current board
      user.setBoardPermissions(board);

      addUserInfoSentry(user);

      if (!(await getRedirectToAgendaIfCantView(user.can(GRANTS.grant_four), board))) {
        window.SwivuAppLoaderInstance.hide();
        return;
      }

      // loading project from projectFactory
      const { newProject, newPlanProject } = createProject(user);
      boardSessionIdRef.current = StringHelper.generateUUID();
      if (!user.isDemoUser && ENABLE_WEBSOCKET) {
        // When the user changes to a different board, the socket connection for the previous board is closed.
        if (swivuWebSocketRef.current) {
          swivuWebSocketRef.current.destroy();
          swivuWebSocketRef.current = null;
        }
        swivuWebSocketRef.current = new SwivuWebSocketService({
          project: newProject, viewManager, sessionId: boardSessionIdRef.current, board, user, boardManager
        });
      }
      newProject.swivuAppRoles = newPlanProject.swivuAppRoles = appRolesRef.current;

      // setting board request url for get and update
      const url = `${SERVER_BASE_URL}/board/${board.slug}`;
      newProject.loadUrl = url;
      newProject.syncUrl = url;
      newProject.transport.load.params = !getIsArchivedForBoard(board.slug) ? { hide_archive: true, board_session_id: boardSessionIdRef.current } : {board_session_id: boardSessionIdRef.current};
      newProject.transport.sync.params = !getIsArchivedForBoard(board.slug) ? { hide_archive: true, board_session_id: boardSessionIdRef.current } : {board_session_id: boardSessionIdRef.current};
      // load
      newProject.load();
      newProject.on({
        beforeLoad() {
          window.SwivuAppLoaderInstance.show();
          setAppLoaded(false);
        },

        load() {
          const boardUser = newProject.boardUsersStore.find((bu) => bu.userId === user.id);
          if (checkUserEmailAndRole(newProject, user, boardUser, { role: { value: [boardUser.roles], oldValue: [board.roles] }})) {
            // this preference is required by backend, generating it ahead of time as not doing so will cause,
            // the notification to not release until these preference is creating from board setting
            if (!userPreferences.getSetting(PERSISTENCE_STATE_KEYS.allowedEmailNotifications)) {
              userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.allowedEmailNotifications, JSON.stringify(Object.values(BACKEND_NOTIFICATION_KEYS)));
            }
            setFontSize(newProject.userPreferencesStore);
            // set user preference state provider
            const storage = initPersistedStorage(user, newProject.userPreferencesStore);
            const stateProvider = new SwivuStateProvider({ storage });
            setGlobalSearch(stateProvider.getValue(PERSISTENCE_STATE_KEYS.globalSearch)?.value || null);
            setStateProvider(stateProvider);

            const viewManagerInstance = new ViewManager(
              newProject.viewsStore,
              newProject.viewsFolderStore,
              userPreferences,
              user,
              board,
              history,
              entityManager,
              newProject
            );
            viewManagerInstance.init();
            setViewManager((previousViewManager) => {
              if (previousViewManager) previousViewManager.cleanUp();
              return viewManagerInstance;
            });

            if (swivuWebSocketRef.current && ENABLE_WEBSOCKET) {
              swivuWebSocketRef.current.viewManager = viewManagerInstance;
              swivuWebSocketRef.current.connect();
            }

            // it's the time to display everything
            window.SwivuAppLoaderInstance.hide();
            setAppLoaded(true);
            newProject.trigger(TriggerKeyForLastUpdatedAt, newProject.boardStore.first)
          }
        },
        loadFail() {
          MessageDialog.alert({
            title: "Cannot Load Board",
            message: `An error occurred while loading the board: ${board.name}. Click "OK" to reload the page. If the message persists, please contact support.`
          }).then(() => window.location.reload())
        },
        beforeSync(e) {
          const syncPack = e.pack && cleanUpOrphanEventsIfAny(e.pack);

          // If there are
          if (syncPack && (Object.keys(syncPack).length < 4 || isReplayHappen(ObjectHelper.clone(syncPack)))) {
            return false; // do not sync if replay happened
          }

          networkStatusStore.updateNetworkRequestStatus(true);
          // Todo: do we need this logic?
          /*if (swivuWebSocketRef.current) swivuWebSocketRef.current.refresh();*/
          window.swivuInProgressNetworkRequests++;
        },
        beforeSyncApply({ response }) {
          // Using beforeSyncApply listener because server will return the current notifyee record if creating or existing, we need to make sure that toast is only shown when record is added
          // Applying check for reminder store because toast should be shown only, if notifyee is added via reminder action
          if (STORE_KEYS.notifyeeBoardUsers in response && STORE_KEYS.reminderStore in response) {
            const { notfiyeeBoardUserStore, boardUsersStore } = newProject;

            const currentBoardUser = boardUsersStore.find((boardUserRecord) => boardUserRecord.userId === user.id);
            const boardUserIdSourceKey = NotifyeeBoardUser.getFieldDefinition("boardUserId").dataSource;
            // Todo: Investigate if board user is added as notifyee in two entities at the same time
            const notifyBoardUserInResponse = response[STORE_KEYS.notifyeeBoardUsers].rows.find(
              notifyeeboardUserRecord => notifyeeboardUserRecord[boardUserIdSourceKey] === currentBoardUser.id
            );
            /* If current board user is not in notify record from response, then don't show reminder update toast */
            if (notifyBoardUserInResponse) {
              /* Checking in notify Board Store if response record exists in the store already or not*/
              let entityMetaInfo = {};
              const isExistingNotifyee = notfiyeeBoardUserStore.find(record => {
                entityMetaInfo = NotifyeeBoardUser.getEntityInformationByRecord(record);
                return record[entityMetaInfo.modelKey] === notifyBoardUserInResponse[entityMetaInfo.sourceKey] && record.boardUserId === notifyBoardUserInResponse[boardUserIdSourceKey]
              });
              /* If current notify board user with reminder updates exists in response but does not exists in store then toast is displayed */
              const reminderUpdatesSourceKey = NotifyeeBoardUser.getFieldDefinition("isReminderUpdates").dataSource;
              if (!isExistingNotifyee?.isReminderUpdates && notifyBoardUserInResponse[reminderUpdatesSourceKey]) {
                showToast({ msg: `${currentBoardUser.name}. You are included in the Reminders Notify list for this ${entityMetaInfo.type}`, type: "success" })
              }
            }
          }
        },
        sync() {
          networkStatusStore.updateNetworkRequestStatus(false);
          // When main project is loaded we need to trigger project recalculation
          // on the plan project too making sure plan view reacts to load (when
          // assignment id is updated on the main project plan view is going to
          // schedule row repaing on project commit)
          newPlanProject.commitAsync();
          newProject.trigger(TriggerKeyForLastUpdatedAt, newProject.boardStore.first)
          window.swivuInProgressNetworkRequests--;
        },
        requestFail(e) {
          if (!e.responseText) return;
          networkStatusStore.updateNetworkRequestStatus(false);
          window.inProgressNetworkRequests = 0;
          window.removeEventListener("beforeunload", canLoadPage);

          const respBody = StringHelper.safeJsonParse(e.responseOptions.body);
          if (!respBody || !e.responseText) return;

          const jsonResponseText = StringHelper.safeJsonParse(e.responseText);
          const jsonResponseKeys =
            typeof jsonResponseText === "string" ? { noKey: jsonResponseText } : Object.keys(jsonResponseText);

          if (isOnlyUserPreferencesCall(respBody)) {
            logSyncErrorToSentry(e, newProject.changes);
            showRequestFailErrorDialog(e, jsonResponseKeys, newProject, newProject.changes, jsonResponseText);
            return;
          }

          const respStatusCode = e.responseOptions.swivuRespStatusCode;
          if (respStatusCode === 401) {
            showSessionExpiredAlert();
          } else if (respStatusCode >= 500) {
            SwivuRootMask.mask({
              cls: 'swivu-out-of-sync-mask',
              text: "Your Swivu schedule is out of sync due to a 500 error code from an API failure. Reload page to synchronize your schedule.",
              style: "z-index: 20000;"
            });
          } else {
            let excludeUserPrefChanges = { ...newProject.changes };
            if (excludeUserPrefChanges.userpreferences) {
              delete excludeUserPrefChanges.userpreferences;
            }

            logSyncErrorToSentry(e, excludeUserPrefChanges);
            showRequestFailErrorDialog(e, jsonResponseKeys, newProject, excludeUserPrefChanges, jsonResponseText);
          }
        },
      });
    },
    [createProject, user]
  );

  /**
   * @description - our board manager is responsible for managing all board operations, such as CRUD
   */
  const boardManager = useBoards({
    userIsAuthenticated: Boolean(user),
    onBoardSelected,
    appRolesRef
  });

  // This function will run on logout to destroy all stores and states
  const cleanLoadProject = useCallback(() => {
    // Hang on to current projects and stores for later destruction.
    const prevProjectsAndStores = { project, planProject, extraStores };
    createProject();
    boardManager.clearSelectedBoard();
    destroyProjectsAndStores(prevProjectsAndStores, boardManager);
  }, [boardManager, createProject, extraStores, planProject, project]);
  useEffect(() => {
    // Whenever user logs out, create a new project.
    if (!user && isAppLoaded) {
      cleanLoadProject();
      window.SwivuAppLoaderInstance.show();
      setAppLoaded(false);
      editorHandler.closeAllEditors();
    } else if (!isAppLoaded && editorHandler.isEditorsOpened()) {
      // in case, if user switches board then close all opened editors
      editorHandler.closeAllEditors();
    }
  }, [boardManager, cleanLoadProject, editorHandler, isAppLoaded, user]);

  // we need a function accessible both in React context and in Bryntum
  // components that updates a setting in userPreferencesStore
  const updateSetting = useCallback(
    (key, value, isBoardPref = false) => {
      // only update a board type of preference if a user has a write permission
      if (isBoardPref && user && !user.can(GRANTS.grant_one)) return;
      key = key?.name ? key.name : key;
      const prefStore = isBoardPref ? userPreferences.boardPreferencesStore: userPreferences;
      const record = prefStore.find((r) => r.key === key);
      if (record) {
        record.value = value;
      } else {
        prefStore.add({ key, value, user: user.id });
      }
    },
    [user]
  );

  const deleteSetting = useCallback(
    (key, isBoardPref = false) => {
      // only update a board type of preference if a user has a write permission
      if (isBoardPref && user && !user.can(GRANTS.grant_one)) return;
      key = key?.name ? key.name : key;
      const prefStore = isBoardPref ? userPreferences.boardPreferencesStore: userPreferences;
      const record = prefStore.find((r) => r.key === key);
      if (record) {
        record.remove()
      }
    },
    [user]
  );

  // set userPreferences when project changes
  useEffect(() => {
    userPreferences = project?.userPreferencesStore;
    if (userPreferences) {
      userPreferences.boardPreferencesStore = project?.boardPreferencesStore;
      userPreferences.updateSetting = updateSetting;
      userPreferences.getSetting = getSetting;
      userPreferences.deleteSetting = deleteSetting;
    }
  }, [project, updateSetting, deleteSetting]);

  useEffect(() => {

    const unhandledRejectionListener = unHandleErrorListener(project);
    window.addEventListener("unhandledrejection", unhandledRejectionListener);

    if (project && !isBeforeUnloadEventAdded) {
      isBeforeUnloadEventAdded = true;
      window.addEventListener("beforeunload", canLoadPage);
    }

    return () => {
      window.removeEventListener("unhandledrejection", unhandledRejectionListener)
      if (isBeforeUnloadEventAdded) {
        isBeforeUnloadEventAdded = false;
        window.removeEventListener("beforeunload", canLoadPage);
      }
    }
  }, [project]);

  const reloadProjectData = () => {
    // cannot use project.load() as after load the nested relation items disappeared, as per investigation the issue turned out
    // that archive filter in store might be causing the issue when useEnableStateTracking reassign the state
    // if (project) return project.load();
    return onBoardSelected(boardManager.selectedBoard.record);
  }

  const swivuAppGrants = useMemo(() => {
    if (!project || !project.swivuAppRoles || !user) return [];
    return project.swivuAppRoles
      .filter((role) => role.grants.length)
      .map((role) => {
        const grantValue = Math.min.apply(
          Math,
          role.grants.map((g) => g.value)
        );
        return {
          label: `${role.name} +`,
          value: grantValue,
          disabled: !user.can(grantValue) // disable options higher than user role
        }
      });
  }, [project, project?.swivuAppRoles, user]);

  return (
    <BryntumContext.Provider
      value={{
        swivuWebSocketRef,
        isAppLoaded,
        boardManager,
        project,
        planProject,
        stateProvider,
        viewManager,
        editorHandler,
        selectedBoard: boardManager.selectedBoard,
        user,
        setFontSize,
        userPreferences,
        getSetting,
        deleteSetting,
        updateSetting,
        getRowHeight,
        getResourceMargin,
        getStripeFeature,
        reloadProjectData,
        globalSearch,
        setGlobalSearch,
        currentViewRef,
        history,
        boardSessionIdRef,
        swivuAppGrants,
        entityManager
      }}
    >
      {props.children}
    </BryntumContext.Provider>
  );
};

export default BryntumContext;

export {
  BryntumContextProvider,
  userPreferences,
  getSetting,
  getRowHeight,
  getFontSize,
  setFontSize,
  getGridLinesPreferenceSetting
};
